import type { VectorStyle } from "@fortex/api";
import type { CatalogueEntry } from "../catalogue/types";
import type { FeatureStyle, RenderState } from "./types";

export const layerPalette = ["#2563eb", "#db2777", "#059669", "#d97706", "#7c3aed", "#0891b2", "#dc2626", "#4d7c0f"];

export const selectionColor = "#C2255C";

export const selectionCasingColor = "#FFFFFF";

export const hoverHaloColor = "#0E6B7A";

export const defaultLineWeight = 3;

export const defaultPolygonWeight = 2;

export const defaultFillOpacity = 0.15;

export const defaultPointRadius = 6;

const hexColor = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;

export function paletteColor(index: number): string {
  return layerPalette[((index % layerPalette.length) + layerPalette.length) % layerPalette.length];
}

export function normaliseColor(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;

  const trimmed = value.trim();

  if (hexColor.test(trimmed)) return trimmed.length === 9 ? trimmed.slice(0, 7) : trimmed;

  return trimmed.length > 0 ? trimmed : fallback;
}

export function alphaOf(value: string | null | undefined): number | null {
  if (!value || !hexColor.test(value.trim()) || value.trim().length !== 9) return null;

  return Number.parseInt(value.trim().slice(7, 9), 16) / 255;
}

export function paletteIndexOf(entry: CatalogueEntry): number {
  return Math.abs(entry.id) % layerPalette.length;
}

export function entryColor(entry: CatalogueEntry, index: number = paletteIndexOf(entry)): string {
  return normaliseColor(entry.style?.pen?.color ?? entry.style?.brush?.color ?? null, paletteColor(index));
}

export function baseStyle(color: string): FeatureStyle {
  return {
    color,
    weight: defaultLineWeight,
    opacity: 0.9,
    dashArray: null,
    lineCap: "round",
    fillColor: color,
    fillOpacity: defaultFillOpacity,
    fill: true,
    radius: defaultPointRadius,
    iconUrl: null,
    iconSize: null,
    iconAnchor: null,
    mark: "circle",
    label: null
  };
}

export function dashArrayOf(penType: number | null | undefined): string | null {
  switch (penType) {
    case 1:
      return "8 6";
    case 2:
      return "2 4";
    case 3:
      return "8 4 2 4";
    case 4:
      return "8 4 2 4 2 4";
    default:
      return null;
  }
}

export function styleFromEntry(entry: CatalogueEntry, index: number = paletteIndexOf(entry)): Partial<FeatureStyle> {
  const color = entryColor(entry, index);
  const style: Partial<FeatureStyle> = { color, fillColor: color };
  const pen = entry.style?.pen ?? null;
  const brush = entry.style?.brush ?? null;
  const icon = entry.style?.icon ?? null;

  if (pen) {
    style.weight = pen.width > 0 ? pen.width : defaultLineWeight;
    style.dashArray = dashArrayOf(pen.type);

    const alpha = alphaOf(pen.color);

    if (alpha !== null) style.opacity = alpha;
  }

  if (brush) {
    style.fillColor = normaliseColor(brush.color, color);
    style.fillOpacity = brush.opacity;
    style.fill = brush.style !== 0;
  }

  if (icon) {
    style.iconUrl = icon.url;
    style.iconSize = [icon.width, icon.height];
    style.iconAnchor = icon.anchor;
    style.mark = "icon";
  }

  return style;
}

function markOf(value: string | null | undefined): FeatureStyle["mark"] {
  const mark = (value ?? "").toLowerCase();

  if (mark.includes("square")) return "square";
  if (mark.includes("triangle")) return "triangle";
  if (mark.length === 0) return null;

  return "circle";
}

export function styleFromVector(styles: VectorStyle[] | null | undefined, fallback: string): Partial<FeatureStyle> {
  const result: Partial<FeatureStyle> = {};

  for (const style of styles ?? []) {
    if (style.stroke) {
      result.color = normaliseColor(style.stroke.color, fallback);
      result.weight = style.stroke.width > 0 ? style.stroke.width : defaultLineWeight;
      result.opacity = style.stroke.opacity;
      result.dashArray = style.stroke.dash && style.stroke.dash.length > 0 ? style.stroke.dash.join(" ") : null;
      result.lineCap = style.stroke.lineCap === "butt" || style.stroke.lineCap === "square" ? style.stroke.lineCap : "round";
    }

    if (style.fill) {
      result.fillColor = normaliseColor(style.fill.color, fallback);
      result.fillOpacity = style.fill.opacity;
      result.fill = style.fill.opacity > 0;
    }

    if (style.graphic) {
      if (style.graphic.iconUrl) {
        result.iconUrl = style.graphic.iconUrl;
        result.iconSize = [style.graphic.size, style.graphic.size];
        result.iconAnchor = style.graphic.anchor && style.graphic.anchor.length === 2 ? [style.graphic.anchor[0], style.graphic.anchor[1]] : [style.graphic.size / 2, style.graphic.size / 2];
        result.mark = "icon";
      } else {
        result.mark = markOf(style.graphic.mark) ?? "circle";
        result.radius = style.graphic.size > 0 ? style.graphic.size / 2 : defaultPointRadius;

        if (style.graphic.fill) result.fillColor = normaliseColor(style.graphic.fill.color, result.fillColor ?? fallback);
        if (style.graphic.stroke) result.color = normaliseColor(style.graphic.stroke.color, result.color ?? fallback);
      }
    }

    if (style.label) {
      result.label = {
        text: style.label.text,
        color: normaliseColor(style.label.color, "#1F2933"),
        halo: style.label.halo ? normaliseColor(style.label.halo, "#FFFFFF") : null,
        size: style.label.size > 0 ? style.label.size : 12,
        bold: style.label.bold,
        offset: [style.label.offsetX, style.label.offsetY]
      };
    }
  }

  return result;
}

export function mergeStyle(base: FeatureStyle, ...layers: (Partial<FeatureStyle> | null | undefined)[]): FeatureStyle {
  let result: FeatureStyle = { ...base };

  for (const layer of layers) {
    if (!layer) continue;

    const entries = Object.entries(layer).filter(([, value]) => value !== undefined) as [keyof FeatureStyle, unknown][];

    result = { ...result, ...(Object.fromEntries(entries) as Partial<FeatureStyle>) };
  }

  return result;
}

export function hoverDelta(style: FeatureStyle): Partial<FeatureStyle> {
  return { weight: style.weight + 2, fillOpacity: Math.min(1, style.fillOpacity + 0.15), radius: style.radius + 2 };
}

export function selectedDelta(style: FeatureStyle): Partial<FeatureStyle> {
  return { color: selectionColor, weight: style.weight + 1, fillColor: selectionColor, fillOpacity: Math.min(1, Math.max(style.fillOpacity, 0.2)), radius: style.radius + 2 };
}

export function resolveStyle(style: FeatureStyle, state: RenderState): FeatureStyle {
  if (state === "hover") return mergeStyle(style, hoverDelta(style));
  if (state === "selected") return mergeStyle(style, selectedDelta(style));

  return style;
}

export type HaloStyle = { color: string; weight: number; opacity: number; radius: number };

export function haloStyle(style: FeatureStyle, state: RenderState): HaloStyle | null {
  if (state === "hover") return { color: hoverHaloColor, weight: style.weight + 6, opacity: 0.35, radius: style.radius + 6 };
  if (state === "selected") return { color: selectionCasingColor, weight: style.weight + 5, opacity: 0.95, radius: style.radius + 5 };

  return null;
}
